/**
 * Config Service
 *
 * Serviço para carregar configurações do backend Python.
 * Inclui tema, feature flags e configurações gerais.
 *
 * @since 2025-12-16
 * @epic INIT-0045-03, INIT-0046
 */

import type { Theme } from '@/theme/types';

// ==========================================
// TYPES
// ==========================================

export interface BootstrapConfig {
  theme: Theme;
  features: {
    darkMode: boolean;
    mfa: boolean;
    socialLogin: boolean;
    registration: boolean;
  };
  config: {
    appName: string;
    language: string;
    debug: boolean;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  request_id: string;
}

// ==========================================
// CONFIG SERVICE
// ==========================================

/**
 * Base URL para API Python
 * Usa /api/v1 que é proxied pelo Vite para o backend Python
 */
const PYTHON_API_BASE = '/api/v1/content';

/**
 * A API Python já retorna URLs completas para imagens.
 * Esta função é mantida para compatibilidade mas apenas retorna o tema sem modificação.
 */
function processThemeImageUrls(theme: Theme): Theme {
  // Python API já retorna URLs completas do CDN
  // Apenas retorna o tema como recebido
  return theme;
}

/**
 * Busca configuração do tema do backend Python
 *
 * Endpoint: GET /api/v1/content/themes/default
 * Retorna tema parseado no formato esperado pelo React
 */
export async function fetchThemeConfig(): Promise<Theme | null> {
  try {
    const response = await fetch(`${PYTHON_API_BASE}/themes/default?_t=${Date.now()}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      if (import.meta.env.DEV) console.warn('Failed to fetch theme config from Python API:', response.status);
      return null;
    }

    const data = await response.json();

    // API Python retorna diretamente o tema parseado
    if (data && data.id && data.name) {
      // Processa URLs de imagens
      return processThemeImageUrls(data as Theme);
    }

    return null;
  } catch (error) {
    if (import.meta.env.DEV) console.warn('Error fetching theme config from Python API:', error);
    return null;
  }
}

/**
 * Busca configuração completa de bootstrap do backend
 */
export async function fetchBootstrapConfig(): Promise<BootstrapConfig | null> {
  try {
    const response = await fetch(`${PYTHON_API_BASE}/bootstrap`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      if (import.meta.env.DEV) console.warn('Failed to fetch bootstrap config:', response.status);
      return null;
    }

    const data: ApiResponse<BootstrapConfig> = await response.json();

    if (data.success && data.data) {
      return data.data;
    }

    return null;
  } catch (error) {
    if (import.meta.env.DEV) console.warn('Error fetching bootstrap config:', error);
    return null;
  }
}

/**
 * Hook para usar com React Query
 */
export const configQueryKeys = {
  theme: ['config', 'theme'] as const,
  bootstrap: ['config', 'bootstrap'] as const,
};
